<script setup lang="ts">
import MarqueeText from '@monogrid/vue-boost/core/MarqueeText.vue'
import { usePreferredReducedMotion, useTimeout } from '@vueuse/core'
import gsap from 'gsap'
import { computed } from 'vue'

import loadingAnimation from '@/assets/svg/lottie/loading.json'
import LottieAnimation from '@/components/atoms/LottieAnimation.vue'
import { isAssetsLoaded, isPreloaderGone } from '@/constants'
import type { LottieAnimationData } from '@/types'

const fakeDuration = 2000
const marqueeSquares = [
  '#616A37',
  '#D9CDAD',
  '#23280B',
  '#616A37',
  '#D9CDAD',
  '#23280B'
]

const animationData = loadingAnimation as LottieAnimationData
const prefersReducedMotion = usePreferredReducedMotion()
const hasFakeDurationElapsed = useTimeout(fakeDuration)
const isReducedMotion = computed(() => prefersReducedMotion.value === 'reduce')
const visible = computed(() => !isAssetsLoaded.value || !hasFakeDurationElapsed.value)

function getTargets (el: Element): {
  root: HTMLElement
  lottie: HTMLElement | null
  topRibbon: HTMLElement | null
  bottomRibbon: HTMLElement | null
} {
  const root = el as HTMLElement

  return {
    root,
    lottie: root.querySelector<HTMLElement>('[data-preloader-lottie]'),
    topRibbon: root.querySelector<HTMLElement>('[data-preloader-top-ribbon]'),
    bottomRibbon: root.querySelector<HTMLElement>('[data-preloader-bottom-ribbon]')
  }
}

function animateEnter (el: Element, done: () => void): void {
  const { root, lottie, topRibbon, bottomRibbon } = getTargets(el)

  if (isReducedMotion.value || !lottie || !topRibbon || !bottomRibbon) {
    gsap.set(root, { autoAlpha: 1 })
    gsap.set(lottie, { clipPath: 'inset(0 0% 0 0)' })
    gsap.set(topRibbon, { '--ribbon-x': '0%' })
    gsap.set(bottomRibbon, { '--ribbon-x': '0%' })
    done()
    return
  }

  gsap.timeline({ onComplete: done })
    .set(root, { autoAlpha: 1 })
    .set(lottie, { clipPath: 'inset(0 100% 0 0)' })
    .set(topRibbon, { '--ribbon-x': '-100%' })
    .set(bottomRibbon, { '--ribbon-x': '100%' })
    .to(lottie, {
      clipPath: 'inset(0 0% 0 0)',
      duration: 0.45,
      ease: 'expo.inOut'
    })
    .to([topRibbon, bottomRibbon], {
      '--ribbon-x': '0%',
      'duration': 0.55,
      'ease': 'expo.inOut',
      'stagger': 0.04
    }, '-=0.12')
}

function animateLeave (el: Element, done: () => void): void {
  const { root, lottie, topRibbon, bottomRibbon } = getTargets(el)

  const finish = (): void => {
    isPreloaderGone.value = true
    done()
  }

  if (isReducedMotion.value || !lottie || !topRibbon || !bottomRibbon) {
    gsap.to(root, {
      yPercent: -100,
      duration: 0.2,
      ease: 'expo.inOut',
      onComplete: () => {
        gsap.set(root, { autoAlpha: 0 })
        finish()
      }
    })
    return
  }

  gsap.timeline({ onComplete: finish })
    .to(root, {
      yPercent: -100,
      duration: 0.62,
      ease: 'expo.inOut'
    })
    .set(root, { autoAlpha: 0 })
}

defineExpose({ visible })
</script>

<template>
  <Transition
    appear
    :css="false"
    @enter="animateEnter"
    @leave="animateLeave"
  >
    <section
      v-if="visible"
      id="app-loading"
      class="preloader"
      role="status"
      aria-label="Loading experience"
    >
      <div
        class="ribbon ribbon--top"
        data-preloader-top-ribbon
        aria-hidden="true"
      >
        <MarqueeText :speed-factor="isReducedMotion ? 99999 : 5">
          <span class="ribbon-content">
            <span
              v-for="(squareColor, index) in marqueeSquares"
              :key="`top-square-${index}`"
              class="square"
              :style="{ backgroundColor: squareColor }"
            />
          </span>
        </MarqueeText>
      </div>

      <div
        class="lottie-wrap"
        data-preloader-lottie
      >
        <LottieAnimation :animation-data="animationData" />
      </div>

      <div
        class="ribbon ribbon--bottom"
        data-preloader-bottom-ribbon
        aria-hidden="true"
      >
        <MarqueeText :speed-factor="isReducedMotion ? 99999 : 5">
          <span class="ribbon-content">
            <span
              v-for="(squareColor, index) in marqueeSquares"
              :key="`bottom-square-${index}`"
              class="square"
              :style="{ backgroundColor: squareColor }"
            />
          </span>
        </MarqueeText>
      </div>
    </section>
  </Transition>
</template>

<style lang="scss" scoped>
.preloader {
  position: fixed;
  z-index: 50;
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  background: #465016;
  inset: 0;
}

.ribbon {
  position: absolute;
  left: 0;
  overflow: hidden;
  width: 100%;
  height: 82px;
  pointer-events: none;
}

.ribbon--top {
  top: 0;
}

.ribbon--bottom {
  bottom: 0;
}

.ribbon-content {
  display: flex;
  height: 100%;
  flex-shrink: 0;
  align-items: center;
  padding-right: 24px;
  padding-left: 24px;
  gap: 42px;
}

.square {
  display: block;
  width: 48px;
  height: 82px;
  flex-shrink: 0;
}

.lottie-wrap {
  position: relative;
  z-index: 1;
  overflow: hidden;
  width: 50%;

  @screen md {
    width: 35%;
  }
}

[data-preloader-lottie] {
  clip-path: inset(0 100% 0 0);
}

[data-preloader-top-ribbon] {
  --ribbon-x: -100%;

  transform: translateX(var(--ribbon-x)) rotate(2deg);
}

[data-preloader-bottom-ribbon] {
  --ribbon-x: 100%;

  transform: translateX(var(--ribbon-x)) rotate(-3deg);
}

.ribbon :deep(.vue-boost-marquee-container) {
  height: 100%;
}
</style>
